import {
    Box,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    styled,
    Typography,
} from '@mui/material'
import SimpleMRTTable from '../../../Common/SimpleTableNonInfinite'
import { MRT_Cell, MRT_ColumnDef } from 'material-react-table'
import { InTransitSettingsType } from '../../../Applications/Intransit/interfaces'
import { RelatedEntityInstance } from '../../../../services/apiInterfaces'
import { getAttributeById } from '../../../../utils/AttributeUtils'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import {
    ShippingRoute,
    Location,
    Container,
    Vessel,
    IRouteTableData,
    IVesselTableData,
    IContainerTableData,
    IEventsTableData,
    Event,
} from '../../interfaces'
import {
    capitalizeFirstLetterOfWord,
    convertNumberToString,
    returnNullIfStringifiedNull,
} from '../../../../utils/StringUtils'
import { useAppSelector } from '../../../../redux/hook'
import { convertAndFormatDateForMaritime } from '../../../../utils/DateAndTimeUtils'
import { useTranslation } from 'react-i18next'
import TruncatedText from '../../../Common/TruncatedText'

const getDescriptionValue = (event: Event) => {
    const parts: string[] = []
    if (event.description) {
        parts.push(capitalizeFirstLetterOfWord(event.description))
    }
    if (event.status) {
        parts.push(`(${event.status})`)
    }
    return parts.join(' ')
}

const DescriptionCell = ({
    cell,
}: {
    cell: MRT_Cell<IEventsTableData, unknown>
}) => <TruncatedText text={cell.getValue<string>()} />

const MuiTableContainer = styled((props: { children: ReactElement }) => (
    <Box {...props} />
))(() => ({
    marginTop: '4px',
    marginBottom: '16px',
}))

interface IMaritimeReport {
    inTransitSettings: InTransitSettingsType
    searatesEntityInstances: RelatedEntityInstance[]
}

const MaritimeReport = (props: IMaritimeReport): ReactElement => {
    const { inTransitSettings, searatesEntityInstances } = props
    const {
        routeAttributeId,
        locationsAttributeId,
        containersAttributeId,
        vesselsAttributeId,
    } = inTransitSettings

    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )

    const [selectedContainerNumber, setSelectedContainerNumber] = useState<
        string | null
    >(null)

    const [isLoadingContainerChange, setIsLoadingContainerChange] =
        useState(false)

    /**
     * TODO: Later select searatesEntityInstance from the selectedValue state in Graph.tsx. We should select the first
     * checked seartes instance. Otherwise we should bydefault select the searatesInstance at 0th index of searatesEntityInstances
     */
    const searatesEntityInstance = searatesEntityInstances?.[0]
    const { t } = useTranslation()
    const {
        routeAttributeValue,
        locationsAttributeValue,
        containersAttributeValue,
        vesselsAttributeValue,
    } = useMemo(() => {
        const routeAttributeValue: ShippingRoute =
            returnNullIfStringifiedNull(
                getAttributeById(
                    searatesEntityInstance?.attributes || [],
                    routeAttributeId,
                )?.value,
            ) || {}

        const locationsAttributeValue: Location[] =
            returnNullIfStringifiedNull(
                getAttributeById(
                    searatesEntityInstance?.attributes || [],
                    locationsAttributeId,
                )?.value,
            ) || []

        const containersAttributeValue: Container[] =
            returnNullIfStringifiedNull(
                getAttributeById(
                    searatesEntityInstance?.attributes || [],
                    containersAttributeId,
                )?.value,
            ) || []

        const vesselsAttributeValue: Vessel[] =
            returnNullIfStringifiedNull(
                getAttributeById(
                    searatesEntityInstance?.attributes || [],
                    vesselsAttributeId,
                )?.value,
            ) || []
        return {
            routeAttributeValue,
            locationsAttributeValue,
            containersAttributeValue,
            vesselsAttributeValue,
        }
    }, [
        searatesEntityInstance?.attributes,
        routeAttributeId,
        locationsAttributeId,
        containersAttributeId,
        vesselsAttributeId,
    ])

    // filter container numbers for the select dropdown
    const containerOptions = useMemo(() => {
        const uniqueContainerNumbers = new Set<string>()
        containersAttributeValue?.forEach((container) => {
            uniqueContainerNumbers.add(container.number)
        })
        return Array.from(uniqueContainerNumbers).map((number) => ({
            value: number,
            label: number,
        }))
    }, [containersAttributeValue])

    // set the first container number as default selection
    useEffect(() => {
        if (containerOptions.length > 0 && !selectedContainerNumber) {
            setSelectedContainerNumber(containerOptions[0].value)
        }
    }, [containerOptions, setSelectedContainerNumber, selectedContainerNumber])

    const getLocationById = useCallback(
        (id: number | null) => {
            if (id === null) {
                return {} as Location
            }
            const location = locationsAttributeValue.find(
                (loc) => loc.id === id,
            )
            return location || ({} as Location)
        },
        [locationsAttributeValue],
    )

    const routeTableData: IRouteTableData[] = useMemo(() => {
        const routeEntries = Object.entries(routeAttributeValue ?? {})

        // sorting the entries based on date
        routeEntries.sort(([, a], [, b]) => {
            const dateA = a?.date
                ? new Date(a.date).getTime()
                : Number.MAX_SAFE_INTEGER
            const dateB = b?.date
                ? new Date(b.date).getTime()
                : Number.MAX_SAFE_INTEGER
            return dateA - dateB
        })
        return routeEntries.map(([key, value]) => {
            const { location: locationId, date, actual } = value ?? {}
            const location = getLocationById(locationId)

            // searates names for the keys
            let displayName = ''
            switch (key.toLowerCase()) {
                case 'pod':
                    displayName = t('portOfDischarge')
                    break
                case 'pol':
                    displayName = t('portOfLoading')
                    break
                case 'prepol':
                    displayName = t('placeOfDispatch')
                    break
                case 'postpod':
                    displayName = t('destination')
                    break
                default:
                    displayName = capitalizeFirstLetterOfWord(key) // Fallback
            }
            return {
                name: displayName,
                location: capitalizeFirstLetterOfWord(location?.name),
                estimatedDate: actual
                    ? ''
                    : convertAndFormatDateForMaritime(date, userLocalization),

                actualDate: actual
                    ? convertAndFormatDateForMaritime(date, userLocalization)
                    : '',
                state: capitalizeFirstLetterOfWord(location?.state),
                country: capitalizeFirstLetterOfWord(location?.country),
                locode: capitalizeFirstLetterOfWord(location?.locode),
            }
        })
    }, [getLocationById, routeAttributeValue, userLocalization, t])

    const routeTableColumns: MRT_ColumnDef<IRouteTableData>[] = useMemo(
        () => [
            { accessorKey: 'name', header: t('event') },
            { accessorKey: 'location', header: t('location') },
            { accessorKey: 'state', header: t('state') },
            { accessorKey: 'country', header: t('country') },
            { accessorKey: 'locode', header: t('locode') },
            { accessorKey: 'estimatedDate', header: t('estimatedDate') },
            { accessorKey: 'actualDate', header: t('actualDate') },
        ],
        [t],
    )

    const vesselTableData: IVesselTableData[] = useMemo(
        () =>
            vesselsAttributeValue?.map((vessel) => ({
                name: capitalizeFirstLetterOfWord(vessel.name),
                imo: convertNumberToString(vessel.imo),
                callSign: capitalizeFirstLetterOfWord(vessel.call_sign),
                mmsi: convertNumberToString(vessel.mmsi),
                flag: capitalizeFirstLetterOfWord(vessel.flag),
            })) || [],
        [vesselsAttributeValue],
    )
    const vesselTableColumns: MRT_ColumnDef<IVesselTableData>[] = useMemo(
        () => [
            { accessorKey: 'name', header: t('name') },
            { accessorKey: 'imo', header: t('imo') },
            { accessorKey: 'callSign', header: t('callSign') },
            { accessorKey: 'mmsi', header: t('mmsi') },
            { accessorKey: 'flag', header: t('flag') },
        ],
        [t],
    )

    const containerTableData: IContainerTableData[] = useMemo(
        () =>
            containersAttributeValue?.map((container) => ({
                containerNumber: capitalizeFirstLetterOfWord(container.number),
                isoCode: capitalizeFirstLetterOfWord(container.iso_code),
                status: capitalizeFirstLetterOfWord(container.status),
                size: capitalizeFirstLetterOfWord(container.size_type),
            })) ?? [],
        [containersAttributeValue],
    )

    const containerTableColumns: MRT_ColumnDef<IContainerTableData>[] = useMemo(
        () => [
            { accessorKey: 'containerNumber', header: t('containerNumber') },
            { accessorKey: 'isoCode', header: t('isoCode') },
            { accessorKey: 'status', header: t('status') },
            { accessorKey: 'size', header: t('size') },
        ],
        [t],
    )

    const eventsTableData: IEventsTableData[] = useMemo(() => {
        if (
            !containersAttributeValue ||
            containersAttributeValue.length === 0
        ) {
            return []
        }

        const selectedContainer = containersAttributeValue.find(
            (container) => container.number === selectedContainerNumber,
        )

        if (!selectedContainer) {
            return []
        }

        return selectedContainer.events.map((event) => {
            const location = getLocationById(event.location)
            const vessel =
                event.vessel !== null
                    ? vesselsAttributeValue.find((v) => v.id === event.vessel)
                    : null

            return {
                location: `${capitalizeFirstLetterOfWord(
                    location.name,
                )}, ${capitalizeFirstLetterOfWord(location.country)}`,
                description: getDescriptionValue(event),
                estimatedDate: event?.actual
                    ? ''
                    : convertAndFormatDateForMaritime(
                          event?.date,
                          userLocalization,
                      ),
                actualDate: event?.actual
                    ? convertAndFormatDateForMaritime(
                          event?.date,
                          userLocalization,
                      )
                    : '',
                type: capitalizeFirstLetterOfWord(event.type),
                vessel: capitalizeFirstLetterOfWord(vessel?.name || ''),
                voyage: capitalizeFirstLetterOfWord(event.voyage || ''),
            } as IEventsTableData
        })
    }, [
        containersAttributeValue,
        selectedContainerNumber,
        getLocationById,
        vesselsAttributeValue,
        userLocalization,
    ])

    const eventsTableColumns: MRT_ColumnDef<IEventsTableData>[] = useMemo(
        () => [
            { accessorKey: 'location', header: t('location') },
            {
                accessorKey: 'description',
                header: t('description'),
                Cell: DescriptionCell,
                size: 250,
            },
            { accessorKey: 'estimatedDate', header: t('estimatedDate') },
            { accessorKey: 'actualDate', header: t('actualDate') },
            { accessorKey: 'type', header: t('type') },
            { accessorKey: 'vessel', header: t('vessel') },
            { accessorKey: 'voyage', header: t('voyage') },
        ],
        [t],
    )

    const handleContainerChange = useCallback((event: any) => {
        setIsLoadingContainerChange(true)
        setSelectedContainerNumber(event.target.value)
        setTimeout(() => {
            setIsLoadingContainerChange(false)
        }, 300)
    }, [])

    return (
        <Box
            sx={{
                background: '#F5FBFF',
                padding: '16px',
            }}
            width="100%"
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '4px',
                }}
            >
                <Typography
                    component="h3"
                    variant="h6"
                    sx={{ marginBottom: '4px' }}
                >
                    {t('events')}
                </Typography>
                <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                    <InputLabel
                        id="container-select-label"
                        sx={{ fontSize: '1rem' }}
                    >
                        {t('containerNumber')}
                    </InputLabel>
                    <Select
                        labelId="container-select-label"
                        id="container-select"
                        value={selectedContainerNumber || ''}
                        label={t('containerNumber')}
                        onChange={handleContainerChange}
                    >
                        {containerOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <MuiTableContainer>
                <>
                    {isLoadingContainerChange && (
                        <LinearProgress sx={{ mb: 2 }} />
                    )}
                    <SimpleMRTTable
                        data={eventsTableData}
                        columns={eventsTableColumns}
                    />
                </>
            </MuiTableContainer>
            <Typography
                component="h3"
                variant="h6"
                sx={{ marginBottom: '4px' }}
            >
                {t('containers')}
            </Typography>
            <MuiTableContainer>
                <SimpleMRTTable
                    data={containerTableData}
                    columns={containerTableColumns}
                />
            </MuiTableContainer>
            <Typography
                component="h3"
                variant="h6"
                sx={{ marginBottom: '4px' }}
            >
                {t('route')}
            </Typography>
            <MuiTableContainer>
                <SimpleMRTTable
                    data={routeTableData || []}
                    columns={routeTableColumns}
                />
            </MuiTableContainer>
            <Typography
                component="h3"
                variant="h6"
                sx={{ marginBottom: '4px' }}
            >
                {t('vessels')}
            </Typography>
            <MuiTableContainer>
                <SimpleMRTTable
                    data={vesselTableData}
                    columns={vesselTableColumns}
                />
            </MuiTableContainer>
        </Box>
    )
}
export default MaritimeReport
